// frontend/src/modules/projects/components/ManualEntryDialog.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { useApi } from '../../../design-system/useApi';
import { projectsApi } from '../api';
import type { Project, Task } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  defaultProjectId?: string;
  defaultTaskId?: string;
  onAdded: () => void;
}

export function ManualEntryDialog({ open, onOpenChange, defaultProjectId, defaultTaskId, onAdded }: Props) {
  const projectsQ = useApi<{ projects: Project[] }>('/api/projects/projects?status=active');
  const [projectId, setProjectId] = React.useState('');
  const [taskId, setTaskId] = React.useState('');
  const tasksQ = useApi<{ tasks: Task[] }>(projectId ? `/api/projects/tasks?project_id=${projectId}` : null, [projectId]);
  const [startDate, setStartDate] = React.useState('');
  const [startTime, setStartTime] = React.useState('');
  const [endDate, setEndDate] = React.useState('');
  const [endTime, setEndTime] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [billable, setBillable] = React.useState(true);
  const [hourlyRate, setHourlyRate] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setProjectId(defaultProjectId ?? '');
    setTaskId(defaultTaskId ?? '');
    const today = new Date().toISOString().slice(0, 10);
    setStartDate(today); setStartTime('09:00');
    setEndDate(today); setEndTime('17:00');
    setNotes('');
    setBillable(true);
    setHourlyRate('');
  }, [open, defaultProjectId, defaultTaskId]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!projectId) { toast.error('Pick a project'); return; }
    const start = new Date(`${startDate}T${startTime}:00`).toISOString();
    const end   = new Date(`${endDate}T${endTime}:00`).toISOString();
    if (new Date(end).getTime() <= new Date(start).getTime()) {
      toast.error('End must be after start'); return;
    }
    setBusy(true);
    try {
      await projectsApi.addManualEntry({
        project_id: projectId,
        task_id: taskId || null,
        start_at: start,
        end_at: end,
        notes: notes || null,
        billable,
        hourly_rate: hourlyRate.trim() ? Number(hourlyRate) : null,
      });
      toast.success('Entry added');
      onAdded();
    } catch (err: any) {
      toast.error('Add failed', { description: err.message });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add manual entry</DialogTitle>
          <DialogDescription>Use this to log time you forgot to track live.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4 pt-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Project</Label>
              <Select value={projectId} onValueChange={(v) => { setProjectId(v); setTaskId(''); }}>
                <SelectTrigger><SelectValue placeholder="Project" /></SelectTrigger>
                <SelectContent>
                  {(projectsQ.data?.projects ?? []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Task (optional)</Label>
              <Select value={taskId} onValueChange={setTaskId} disabled={!projectId}>
                <SelectTrigger><SelectValue placeholder="(none)" /></SelectTrigger>
                <SelectContent>
                  {(tasksQ.data?.tasks ?? []).map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Start date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
            </div>
            <div className="grid gap-1.5">
              <Label>Start time</Label>
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} required />
            </div>
            <div className="grid gap-1.5">
              <Label>End date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required />
            </div>
            <div className="grid gap-1.5">
              <Label>End time</Label>
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 items-end">
            <label className="flex items-center gap-2.5 text-[13px] text-foreground cursor-pointer select-none h-10">
              <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)}
                className="h-4 w-4 rounded border-border accent-saffron" />
              Billable
            </label>
            <div className="grid gap-1.5">
              <Label>Rate override (optional)</Label>
              <Input type="number" min="0" step="0.01" value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} placeholder="Project default" />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Adding…' : 'Add entry'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
